import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import CalculadoraFlex from '../CalculadoraFlex';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'calculadora-flex';

/** Página /ferramentas/calculadora-flex */
const FlexPage: React.FC = () => (
  <>
    <Helmet>
      <title>Calculadora Flex: Álcool ou Gasolina Compensa Hoje? – Gas2</title>
      <meta
        name="description"
        content="Use a calculadora flex gratuita: informe o preço do álcool e da gasolina e descubra qual combustível é mais barato. Com ou sem consumo real do seu carro."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Calculadora Flex: Álcool ou Gasolina Compensa? – Gas2" />
      <meta
        property="og:description"
        content="Calculadora gratuita: álcool ou gasolina — descubra qual combustível compensa hoje com base nos preços do posto e no consumo real do seu carro."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Calculadora Flex: Álcool ou Gasolina? – Gas2" />
      <meta
        name="twitter:description"
        content="Informe os preços do posto e descubra se compensa mais abastecer com álcool ou gasolina hoje."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Economia de Combustível
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora Flex: Álcool ou Gasolina Compensa Hoje?
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Informe os preços do posto e calcule instantaneamente qual combustível é mais
            barato para o seu bolso. Para um resultado ainda mais preciso, adicione o consumo
            real do seu veículo.
          </p>
        </motion.header>

        {/* ── Calculadora ── */}
        <CalculadoraFlex />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como Funciona o Cálculo Flex?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A forma mais simples é a{' '}
              <strong className="text-slate-200">regra dos 70%</strong>: divida o preço do
              álcool pelo preço da gasolina. Se o resultado for menor que 0,70 (70%), o
              álcool compensa. Se for maior, prefira a gasolina. Esse limiar existe porque,
              em média, um carro flex percorre cerca de 30% a menos por litro de etanol do
              que por litro de gasolina.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Porém, essa média nem sempre reflete o seu veículo. Para o cálculo mais
              preciso, use os campos de consumo real: nossa calculadora divide o preço pelo
              consumo de cada combustível, obtendo o{' '}
              <strong className="text-slate-200">custo por quilômetro</strong> — a métrica
              definitiva para comparar combustíveis.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              A Regra dos 70% Ainda Vale em 2026?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Sim, como estimativa geral. No entanto, a proporção ideal varia entre modelos:
              carros mais modernos com injeção direta de etanol chegam a 75–77% de eficiência
              relativa, enquanto modelos mais antigos ficam em 65–68%. Verifique o consumo
              urbano e rodoviário do seu carro no site do INMETRO e use os campos avançados
              desta calculadora para um resultado personalizado.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Por Que o Consumo Real Faz Diferença?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              O consumo real depende de hábitos de direção, trânsito, temperatura e estado
              do veículo. Um carro com filtro de ar sujo, pneus calibrados abaixo do ideal ou
              fluido de arrefecimento degradado pode consumir até 15% a mais. Registre seus
              abastecimentos regularmente no{' '}
              <a href="/" className="text-amber-400 hover:text-amber-300 underline">
                app Gas2
              </a>{' '}
              para ter o consumo médio atualizado automaticamente.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Álcool vs Gasolina
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'O álcool faz mal ao motor?',
                  a: 'Não. Motores flex são projetados para rodar com etanol, gasolina ou qualquer mistura. O etanol, inclusive, lubrifica melhor as peças internas e tem octanagem mais alta.',
                },
                {
                  q: 'Por que o álcool fica mais barato em algumas épocas?',
                  a: 'O preço do etanol varia com a safra da cana-de-açúcar. Entre abril e novembro (período de colheita), os preços costumam cair. Fique de olho e use esta calculadora para pegar o melhor momento.',
                },
                {
                  q: 'Qual combustível emite menos CO₂?',
                  a: 'O etanol emite menos CO₂ líquido por km rodado, pois a cana-de-açúcar absorve CO₂ durante o crescimento. A pegada de carbono do etanol brasileiro é até 70% menor que a da gasolina.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default FlexPage;

